/**
 * In-memory state for one active study session that earns leaderboard
 * MP. Lives outside the study-screen component tree so it survives
 * micro-unmounts within the same React lifecycle and so the post-Summary
 * Reveal page (a separate route) can still read the pre-session
 * snapshot captured on study-screen mount.
 *
 * Shared between mobile and web — the shapes, accumulator rules, and
 * toast guards are identical, so one store keeps both platforms in
 * lockstep (a new field added here flows through to both clients).
 *
 * ### Why a single `activeSession`
 * MVP scope: one device runs one study session at a time. Chain logic
 * is intentionally dropped (no `chainRootSessionId`, no leg-stitching);
 * every fresh `/memo-ai` or `/quiz/*` mount mints a new session id and
 * replaces the active session entirely.
 *
 * ### Per-group MP buckets
 * A card on a deck shared in N groups credits N distinct centi-MP
 * deltas (one per group's pre-bump streak — see `./leaderboard.ts`).
 * Each group's session totals stay independent so the Summary hero /
 * breakdown popover / early-exit fallback can pick the right "dominant"
 * group and display its numbers without mixing groups.
 *
 * ### Global toast guards
 * The milestone-tier-shown + combo counter are USER-FACING (per device,
 * not per group) — a single guard set covers the whole session regardless
 * of how many groups received pushes. No 3-cap, no 8-card cooldown — the
 * tier-based design self-limits (one toast per tier crossing).
 *
 * ### Reveal snapshot
 * Pre-session leaderboard snapshot for the Reveal page. Captured once
 * per `(sessionId, groupId)` on first mount when the viewer entered
 * via a group path. Cleared when the user clicks Continue on Reveal
 * (the only natural exit from the Reveal flow).
 */

import { create } from 'zustand';

import type {
  LeaderboardPeriod,
  LeaderboardResponse,
  ScoreBreakdown,
  ScoreEventType,
} from './scoring';
import type { ToastTrigger } from './toast-rules';
import { decideToast } from './toast-rules';

/** Per-group running totals within ONE study session. Display MP values
 *  are `Math.round(rawSum)`; rawSum keeps the float aggregate so the
 *  round-on-aggregate spec rule holds (server stores integer centi-MP,
 *  but UI aggregates in display MP). */
export type PerGroupAccumulator = {
  /** Float sum across the session for this group, in DISPLAY MP. */
  rawSum: number;
  /** Math.round(rawSum) — the integer the hero / breakdown shows. */
  sessionMP: number;
  /** Cards reviewed in this session that credited THIS group. */
  cardCount: number;
  /** Sum of `eventPoints` (display MP before multiplier) across every card
   *  this session. Mixed event types (SR=3, FR=1, quiz=3, quiz_wrong=1, …)
   *  are tracked exactly here so the breakdown popover can show real
   *  base points instead of approximating `cards × 3`. */
  baseSum: number;
};

export type SessionState = {
  /** uuid/v4-like minted on study-screen mount. */
  sessionId: string;
  /** Group the user entered the session from (URL param or nav state).
   *  `null` when the user entered via /deck/ or a personal-library route —
   *  the hero / sub-line / Reveal CTAs all gate on this being non-null. */
  enteredFromGroupId: string | null;
  /** Set to `true` when the session ends naturally (queue empties on SR/FR,
   *  quiz/exam completion) OR when the user clicks ANY Summary CTA.
   *  Suppresses the early-exit toast — set BEFORE the navigation. */
  naturalEnd: boolean;
  /** Per-group MP accumulators. Keys are `groupId`. */
  perGroup: Record<string, PerGroupAccumulator>;
  /** Highest milestone tier already toasted in this session (0 if none). */
  lastMilestoneShown: number;
  /** Current correct-in-a-row count. Resets on combo-breaking cards. */
  comboCount: number;
  /** `true` after the first card review of this session has been
   *  processed. Drives the streak-multiplier toast watcher in the
   *  lifecycle hook (it only checks liveScore.streak post-first-card). */
  firstCardDone: boolean;
  /** `true` once the queue's last card has been reviewed. Blocks every
   *  subsequent toast emission so the Claim Mastery Points screen
   *  isn't covered by a late toast. */
  lastCardReached: boolean;
  /**
   * Set of dedupe keys that have already been claimed in this session.
   * Quiz / exam call sites pass `dedupeKey: 'q${questionIndex}'` so a
   * spam-click on the same answer doesn't farm MP — every subsequent
   * `pushScoreToAllGroups` for the same question is gated out at the
   * wrapper before the wire send happens. SR / FR pushes never set a
   * dedupe key (the same card can legitimately be re-rated within one
   * session) so they're unaffected.
   *
   * Stored as a Record (not a Set) so the slice stays JSON-serialisable
   * for Zustand's persist / devtools middleware on platforms that wire
   * those up.
   */
  pushedDedupeKeys: Record<string, true>;
};

/** Snapshot map keyed by `${sessionId}::${groupId}` (the Reveal screen
 *  only cares about the entry group; one key per session). */
export type RevealSnapshot = {
  sessionId: string;
  groupId: string;
  capturedAtISO: string;
  response: LeaderboardResponse;
  period: LeaderboardPeriod;
};

export type AddReviewEvent = {
  /** The 7-variant score event type. */
  eventType: ScoreEventType;
  /** `true` when this card SUSTAINS the in-session combo. */
  correct: boolean;
  /** Per-group score breakdowns returned by `pushScoreToAllGroups`. Empty
   *  record → `addReview` is a no-op on the accumulator side. */
  breakdownByGroup: Record<string, ScoreBreakdown>;
  /** `true` when this review is for the LAST card in the session's queue. */
  isLastCard?: boolean;
};

type State = {
  activeSession: SessionState | null;
  snapshots: Record<string, RevealSnapshot>;
  /** Post-session snapshots, captured on Summary mount (pre-fetched in
   *  the background so the Reveal panel can render with the final
   *  headline + row positions on first paint). */
  postSnapshots: Record<string, RevealSnapshot>;
};

type Actions = {
  /** Start (or replace) the active session. New sessionId resets ALL
   *  per-group accumulators + toast guards. Called once on mount. */
  startSession(args: { sessionId: string; enteredFromGroupId: string | null }): void;
  /** Clear `activeSession` and all snapshots for the current session. */
  endSession(): void;
  /** Mark this session as naturally ended. Suppresses the early-exit toast. */
  setNaturalEnd(): void;
  /** Flip the `lastCardReached` flag. Idempotent. */
  setLastCardReached(): void;
  /** Process one per-card review: update per-group accumulators + run
   *  the toast decision pipeline. Returns the toast trigger to fire
   *  (or `null`). Caller is responsible for actually rendering the
   *  toast — keeping the side effect out of the store keeps it testable. */
  addReview(event: AddReviewEvent): ToastTrigger | null;
  /** Persist a pre-session leaderboard snapshot for the Reveal page to
   *  diff against on End Session. Idempotent per `(sessionId, groupId)`. */
  captureSnapshot(snapshot: RevealSnapshot): void;
  /** Persist a post-session leaderboard snapshot pre-fetched on Summary
   *  mount. Idempotent per `(sessionId, groupId)`. */
  capturePostSnapshot(snapshot: RevealSnapshot): void;
  /** Drop a snapshot by `sessionId+groupId`. Called by Reveal Continue. */
  clearSnapshot(args: { sessionId: string; groupId: string }): void;
  /**
   * Atomic check-and-add for a per-session dedupe key. Returns `true`
   * when the key was newly claimed (caller proceeds with the push) and
   * `false` when the key was already present (caller skips the push
   * entirely — no wire, no accumulator update, no toast).
   *
   * Used by quiz / exam push wrappers so a spam-click on the same
   * question answer doesn't farm MP. SR / FR call sites don't set a
   * dedupe key, so they always proceed.
   */
  tryClaimDedupeKey(key: string): boolean;
};

const snapshotKey = (sessionId: string, groupId: string): string =>
  `${sessionId}::${groupId}`;

export const useLeaderboardSessionStore = create<State & Actions>()((set, get) => ({
  activeSession: null,
  snapshots: {},
  postSnapshots: {},

  startSession: ({ sessionId, enteredFromGroupId }) =>
    set((s) => {
      // Evict pre/post snapshots tied to ANY session other than the one
      // we're starting. Natural-end sessions skip `endSession()` and
      // only get cleared when the user taps Continue on Reveal.
      const nextSnapshots: Record<string, RevealSnapshot> = {};
      for (const [k, v] of Object.entries(s.snapshots)) {
        if (v.sessionId === sessionId) nextSnapshots[k] = v;
      }
      const nextPostSnapshots: Record<string, RevealSnapshot> = {};
      for (const [k, v] of Object.entries(s.postSnapshots)) {
        if (v.sessionId === sessionId) nextPostSnapshots[k] = v;
      }
      return {
        activeSession: {
          sessionId,
          enteredFromGroupId,
          naturalEnd: false,
          perGroup: {},
          lastMilestoneShown: 0,
          comboCount: 0,
          firstCardDone: false,
          lastCardReached: false,
          pushedDedupeKeys: {},
        },
        snapshots: nextSnapshots,
        postSnapshots: nextPostSnapshots,
      };
    }),

  endSession: () =>
    set((s) => {
      const prevId = s.activeSession?.sessionId;
      if (!prevId) return { activeSession: null };
      const nextSnapshots: Record<string, RevealSnapshot> = {};
      for (const [k, v] of Object.entries(s.snapshots)) {
        if (v.sessionId !== prevId) nextSnapshots[k] = v;
      }
      const nextPostSnapshots: Record<string, RevealSnapshot> = {};
      for (const [k, v] of Object.entries(s.postSnapshots)) {
        if (v.sessionId !== prevId) nextPostSnapshots[k] = v;
      }
      return {
        activeSession: null,
        snapshots: nextSnapshots,
        postSnapshots: nextPostSnapshots,
      };
    }),

  setNaturalEnd: () =>
    set((s) => {
      if (!s.activeSession) return s;
      return { activeSession: { ...s.activeSession, naturalEnd: true } };
    }),

  setLastCardReached: () =>
    set((s) => {
      if (!s.activeSession || s.activeSession.lastCardReached) return s;
      return { activeSession: { ...s.activeSession, lastCardReached: true } };
    }),

  addReview: (event) => {
    const session = get().activeSession;
    if (!session) return null;
    const groupIds = Object.keys(event.breakdownByGroup);
    if (groupIds.length === 0) {
      // Even a no-MP review (private deck or all sends threw) needs to
      // flip `firstCardDone` so the streak-toast watcher arms itself
      // after card #1.
      if (!session.firstCardDone || (event.isLastCard && !session.lastCardReached)) {
        set(() => ({
          activeSession: {
            ...session,
            firstCardDone: true,
            lastCardReached: session.lastCardReached || !!event.isLastCard,
          },
        }));
      }
      return null;
    }

    // Merge per-group accumulators.
    const nextPerGroup: Record<string, PerGroupAccumulator> = { ...session.perGroup };
    for (const gid of groupIds) {
      const breakdown = event.breakdownByGroup[gid]!;
      const prev = nextPerGroup[gid] ?? {
        rawSum: 0,
        sessionMP: 0,
        cardCount: 0,
        baseSum: 0,
      };
      const rawSum = prev.rawSum + breakdown.centiMP / 100;
      nextPerGroup[gid] = {
        rawSum,
        sessionMP: Math.round(rawSum),
        cardCount: prev.cardCount + 1,
        baseSum: prev.baseSum + breakdown.eventPoints,
      };
    }

    // Pick the dominant group's session MP for milestone evaluation.
    let dominantGroupId: string | null = null;
    let dominantBreakdown: ScoreBreakdown | null = null;
    for (const gid of groupIds) {
      const b = event.breakdownByGroup[gid]!;
      if (dominantBreakdown == null || b.centiMP > dominantBreakdown.centiMP) {
        dominantBreakdown = b;
        dominantGroupId = gid;
      }
    }
    const dominantAccNext = nextPerGroup[dominantGroupId!]!;

    const { trigger, nextGuards } = decideToast({
      guards: {
        lastMilestoneShown: session.lastMilestoneShown,
        comboCount: session.comboCount,
      },
      event: {
        correct: event.correct,
        dominantSessionMPAfter: dominantAccNext.sessionMP,
      },
    });

    // Suppress the per-card toast entirely when this review is for the
    // session's final card OR the session is already past its last card.
    const blockToast = !!event.isLastCard || session.lastCardReached;

    set(() => ({
      activeSession: {
        ...session,
        perGroup: nextPerGroup,
        lastMilestoneShown: nextGuards.lastMilestoneShown,
        comboCount: nextGuards.comboCount,
        firstCardDone: true,
        lastCardReached: session.lastCardReached || !!event.isLastCard,
      },
    }));

    return blockToast ? null : trigger;
  },

  captureSnapshot: (snapshot) =>
    set((s) => {
      const key = snapshotKey(snapshot.sessionId, snapshot.groupId);
      if (s.snapshots[key]) return s;
      return { snapshots: { ...s.snapshots, [key]: snapshot } };
    }),

  capturePostSnapshot: (snapshot) =>
    set((s) => {
      const key = snapshotKey(snapshot.sessionId, snapshot.groupId);
      if (s.postSnapshots[key]) return s;
      return { postSnapshots: { ...s.postSnapshots, [key]: snapshot } };
    }),

  clearSnapshot: ({ sessionId, groupId }) =>
    set((s) => {
      const key = snapshotKey(sessionId, groupId);
      const hasPre = !!s.snapshots[key];
      const hasPost = !!s.postSnapshots[key];
      if (!hasPre && !hasPost) return s;
      const nextPre = { ...s.snapshots };
      const nextPost = { ...s.postSnapshots };
      delete nextPre[key];
      delete nextPost[key];
      return { snapshots: nextPre, postSnapshots: nextPost };
    }),

  tryClaimDedupeKey: (key) => {
    const session = get().activeSession;
    if (!session) {
      // No active session → caller's push will be a no-op anyway, but
      // returning `true` keeps the contract honest: the key wasn't
      // already claimed in some prior session.
      return true;
    }
    if (session.pushedDedupeKeys[key]) return false;
    set(() => ({
      activeSession: {
        ...session,
        pushedDedupeKeys: { ...session.pushedDedupeKeys, [key]: true },
      },
    }));
    return true;
  },
}));

// ─── Selectors ───────────────────────────────────────────────────────

export function useActiveSession(): SessionState | null {
  return useLeaderboardSessionStore((s) => s.activeSession);
}

/** Display MP for a single group within the active session (0 if no
 *  session or no per-group bucket yet). */
export function useSessionMPForGroup(groupId: string | null): number {
  return useLeaderboardSessionStore((s) =>
    groupId && s.activeSession ? (s.activeSession.perGroup[groupId]?.sessionMP ?? 0) : 0,
  );
}

/** Card count for a single group within the active session. */
export function useSessionCardCountForGroup(groupId: string | null): number {
  return useLeaderboardSessionStore((s) =>
    groupId && s.activeSession ? (s.activeSession.perGroup[groupId]?.cardCount ?? 0) : 0,
  );
}

/** `true` once the first card of the active session has been reviewed.
 *  Drives the streak-toast watcher in the lifecycle hook. */
export function useActiveSessionFirstCardDone(): boolean {
  return useLeaderboardSessionStore((s) => s.activeSession?.firstCardDone ?? false);
}

/** `true` once the queue's last card has been reviewed. */
export function useActiveSessionLastCardReached(): boolean {
  return useLeaderboardSessionStore((s) => s.activeSession?.lastCardReached ?? false);
}

export function readSnapshot(
  sessionId: string,
  groupId: string,
): RevealSnapshot | undefined {
  return useLeaderboardSessionStore.getState().snapshots[snapshotKey(sessionId, groupId)];
}

export function readPostSnapshot(
  sessionId: string,
  groupId: string,
): RevealSnapshot | undefined {
  return useLeaderboardSessionStore.getState().postSnapshots[
    snapshotKey(sessionId, groupId)
  ];
}
